import { CLessonInCourse, LESSON_STATUS } from '@yandex-int/k-common';
import b from 'b_';
import cx from 'classnames';
import { Cancel } from 'education-icons';
import { Image, TableCell, TableRow, Text } from 'platform-components';
import { TEXT_MARKUP_TYPE } from 'platform-components/src/components/constants';
import { getIsParent, getIsTeacher } from 'platform-components/src/components/user/selectors';
import React from 'react';
import { useSelector } from 'react-redux';

import LessonSettings from 'common.components/lesson-settings/lesson-settings';
import { isLessonCompleted } from 'common.components/lesson/utils';
import LessonIcon from 'common.components/lessons/__lesson-icon/lessons__lesson-icon';
import LessonLikes from 'common.components/lessons/__lesson-likes/lessons__lesson-likes';
import { getShouldShowLessonMeta } from 'common.components/lessons/selectors';
import { getLessonName, getLessonStatusKey } from 'utils/lesson';
import { getSubjectFromPath } from 'utils/path';

import { Props as CommonProps } from '../calendar-lesson-card/calendar-lesson-card';
import LessonDateAssignment from '../lessons/__lesson-date-assignment/lessons__lesson-date-assignment';

import './list-lesson-item.scss';

interface Props extends CommonProps {
    clickable: boolean;
    shouldDisableClesson: boolean;
    activeSubscriptionSlug: string | null;
}

const getMetaTextId = ({ lesson, mode }: CLessonInCourse) => {
    const methodologicalGoal = lesson.meta?.methodological_goal;

    if (methodologicalGoal) {
        return `lesson.lessonMeta.methodologicalGoals.${methodologicalGoal}`;
    } else if (mode) {
        return `lesson.lessonMeta.mode.${mode}`;
    }

    return null;
};

// eslint-disable-next-line complexity
export const ListLessonItem = ({
    childLink,
    clesson,
    cardsCount,
    onLessonClick,
    onCloseLesson,
    onCopyLesson,
    onLessonDelete,
    courseId,
    shouldDisableSettings,
    lessonPermissions,
    shouldShowLikes,
    likesCount,
    onShowLikesPopup,
    onHideLikesPopup,
    shouldShowSettings,
    onSettingsClick,
    clickable,
    shouldDisableClesson,
    activeSubscriptionSlug,
    setRef,
}: Props) => {
    const {
        status,
        id: clessonId,
        date_deadline: dateDeadline,
        date_assignment: dateAssignment,
        is_read: isRead,
        lesson,
    } = clesson;

    const subject = getSubjectFromPath();
    const isTeacher = useSelector(getIsTeacher);
    const isParent = useSelector(getIsParent);
    const shouldShowLessonMeta = useSelector((store) => getShouldShowLessonMeta(store, { subject }));

    // урок прошлых лет, который был выдан
    const available = isTeacher || !(isParent && status === LESSON_STATUS.ASSIGNED_IN_FUTURE);
    const isCompleted = isLessonCompleted(status);
    const isUnread = isCompleted && !isRead && !isParent;
    const hasIcon = lesson.meta && lesson.meta.lesson_icon;
    const metaTextId = getMetaTextId(clesson);

    const className = b('lessons', 'lesson', {
        'not-clickable': !clickable,
        'not-available': !available,
        'with-meta': shouldShowLessonMeta,
    });

    return (
        <TableRow disabled={shouldDisableClesson} mix={className} onClick={onLessonClick} ref={setRef}>
            {isUnread && <TableCell mix="lessons__unread-lesson-marker" />}
            <TableCell mix="lessons__table-column-type">
                <LessonIcon clesson={clesson} />
            </TableCell>
            <TableCell
                mix={cx(
                    'lessons__table-column-name',
                    b('lessons', 'lesson-name', { 'with-meta': shouldShowLessonMeta })
                )}
            >
                {hasIcon && <Image alt="" className="lessons__lesson-icon" src={lesson.meta.lesson_icon as string} />}
                <div>
                    <Text ellipsis weight={isUnread ? Text.WEIGHT.M : Text.WEIGHT.S}>
                        {getLessonName(lesson)}
                    </Text>
                    {shouldShowLessonMeta && (
                        <div className="lessons__lesson-meta">
                            {metaTextId && (
                                <>
                                    <Text id={metaTextId} />
                                    <span className="lessons__lesson-meta-separator">
                                        <Text id="lessons.lessonMeta.separator" />
                                    </span>
                                </>
                            )}
                            <span className="lessons__lesson-meta-count">
                                <Text id="cardsCount" values={{ cardsCount }} />
                            </span>
                        </div>
                    )}
                </div>
            </TableCell>
            {shouldShowLikes && (
                <TableCell mix="lessons__table-column-like">
                    <LessonLikes
                        clessonId={clessonId}
                        isLarge={true}
                        likesCount={likesCount}
                        onMouseEvent={(target, enter) => {
                            if (enter) {
                                onShowLikesPopup(clesson.id, { current: target });
                            } else {
                                onHideLikesPopup();
                            }
                        }}
                    />
                </TableCell>
            )}
            <TableCell mix={b('lessons', 'table-column-status', { [status]: true })}>
                {status === LESSON_STATUS.CANCELLED && <Cancel />}
                <Text id={getLessonStatusKey(status, false)} type={TEXT_MARKUP_TYPE.MARKDOWN} />
            </TableCell>
            <TableCell mix="lessons__table-column-date-assignment">
                <LessonDateAssignment dateAssignment={dateAssignment} dateDeadline={dateDeadline} />
            </TableCell>
            <TableCell mix="lessons__lesson-settings">
                {shouldShowSettings && (
                    <LessonSettings
                        childLink={childLink}
                        clessonId={clessonId}
                        courseId={courseId}
                        disabled={shouldDisableSettings}
                        lessonPermissions={lessonPermissions}
                        mix="lessons__lesson-settings-dots"
                        onClick={onSettingsClick}
                        onLessonClose={onCloseLesson}
                        onLessonCopy={onCopyLesson}
                        onLessonDelete={onLessonDelete}
                        subscriptionSlug={activeSubscriptionSlug}
                    />
                )}
            </TableCell>
        </TableRow>
    );
};
